// --- MCP: реестр серверов и сетевой доступ (ADR-071 §2.3.1/§2.5) ---
/** Версия схемы записи реестра MCP — для будущей миграции (ADR-096). */
export const MCP_REGISTRY_SCHEMA_VERSION = 1;
export const MCP_REGISTRY_ORIGINS = ['instance', 'user'];
export const MCP_REGISTRY_TRUST_LEVELS = ['builtin', 'community'];
export const MCP_NETWORK_POLICY_MODES = ['allow', 'deny'];
/**
 * Служебные/специальные адресные диапазоны, запрещённые по умолчанию (SSRF/egress).
 *
 * **Единственный источник истины** для всех egress-политик: `mcp.networkPolicy`,
 * `egress.tools`, `egress.webhooks` (`types/egress.ts`) и встроенный fallback
 * guard'а (`core/engine/net/egress-guard.ts` → `isBlockedIp` без политики).
 * Отдельных списков «заблокированных адресов» в коде больше нет: расширять
 * нужно этот список, иначе политика и guard разойдутся (промис не будет
 * подкреплён кодом).
 *
 * **Правило отбора:** диапазоны, которые не маршрутизируются в публичном
 * интернете (IANA IPv4/IPv6 Special-Purpose Address Registry) либо непригодны
 * как цель исходящего HTTP-запроса (multicast, limited broadcast).
 *
 * IPv4:
 * - `0.0.0.0/8` — «this network»; `0.0.0.0` на Linux ведёт на localhost;
 * - `10.0.0.0/8`, `172.16.0.0/12`, `192.168.0.0/16` — RFC1918 (private);
 * - `100.64.0.0/10` — CGNAT (RFC 6598); в ряде облаков это адрес метаданных
 *   (напр. Alibaba `100.100.100.200`);
 * - `127.0.0.0/8` — loopback;
 * - `169.254.0.0/16` — link-local (в т.ч. `169.254.169.254` — метаданные облака);
 * - `192.0.0.0/24` — IETF protocol assignments (RFC 6890), включая well-known
 *   NAT64-префиксы `192.0.0.170/171` (RFC 6052): через них внутренний IPv4
 *   достигается по IPv6-адресу и v4-список его не ловит;
 * - `192.0.2.0/24`, `198.51.100.0/24`, `203.0.113.0/24` — TEST-NET-1/2/3
 *   (RFC 5737): SSRF-риска не несут (не маршрутизируются), но заблокированы
 *   вместе со всеми прочими служебными диапазонами — список самосогласован
 *   («всё специальное»), риск-оценка не нужна по каждому адресу, а вернуть
 *   конкретный адрес можно явным exception политики;
 * - `192.88.99.0/24` — 6to4 relay anycast (RFC 7526, deprecated);
 * - `198.18.0.0/15` — benchmarking (RFC 2544);
 * - `224.0.0.0/4` — multicast: цель для SSRF во внутренние multicast-группы
 *   (mDNS/SSDP и т.п.);
 * - `240.0.0.0/4` — reserved (RFC 1112);
 * - `255.255.255.255/32` — limited broadcast.
 *
 * IPv6:
 * - `::/128` — unspecified, на части стеков эквивалентен loopback;
 * - `::1/128` — loopback;
 * - `::ffff:0:0/96` — IPv4-mapped IPv6. Mapped-запись — типичная попытка обхода
 *   (v4-диапазоны её не ловят), легитимные адреса приходят обычным v4/v6,
 *   поэтому mapped-форма блокируется целиком; `isIpInCidr` дополнительно
 *   приводит mapped к вложенному IPv4 (`::ffff:127.0.0.1` ← `127.0.0.0/8`);
 * - `100::/64` — discard-only (RFC 6666);
 * - `2001:2::/48` — benchmarking (IPv6);
 * - `2001:db8::/32` — documentation (IPv6);
 * - `2002::/16` — 6to4 (RFC 7526, deprecated): адрес вшивает IPv4, поэтому
 *   недостижим для v4-списка;
 * - `fc00::/7` — ULA;
 * - `fe80::/10` — link-local;
 * - `ff00::/8` — multicast.
 *
 * Осознанно НЕ включены (иначе ломается легитимный egress):
 * - `64:ff9b::/96` (NAT64 well-known prefix) и `64:ff9b:1::/48` (local-use
 *   NAT64, RFC 8215) — на IPv6-only сетях с DNS64 резолвер отдаёт
 *   синтезированные AAAA именно из этих префиксов; их блокировка заблокировала
 *   бы весь исходящий трафик к публичным хостам;
 * - глобально маршрутизируемые служебные блоки (`192.31.196.0/24` AS112-v4,
 *   `192.52.193.0/24` AMT, `192.175.48.0/24` AS112, `2001:4860::/32` и т.п.) —
 *   это обычные публичные адреса.
 */
export const DEFAULT_MCP_PRIVATE_CIDRS = [
    '0.0.0.0/8',
    '10.0.0.0/8',
    '100.64.0.0/10',
    '127.0.0.0/8',
    '169.254.0.0/16',
    '172.16.0.0/12',
    '192.0.0.0/24',
    '192.0.2.0/24',
    '192.88.99.0/24',
    '192.168.0.0/16',
    '198.18.0.0/15',
    '198.51.100.0/24',
    '203.0.113.0/24',
    '224.0.0.0/4',
    '240.0.0.0/4',
    '255.255.255.255/32',
    '::/128',
    '::1/128',
    '::ffff:0:0/96',
    '100::/64',
    '2001:2::/48',
    '2001:db8::/32',
    '2002::/16',
    'fc00::/7',
    'fe80::/10',
    'ff00::/8',
];
/** Дефолтная политика: deny служебных диапазонов (ADR-071 §2.3.1). */
export const DEFAULT_MCP_NETWORK_POLICY = {
    mode: 'deny',
    list: [...DEFAULT_MCP_PRIVATE_CIDRS],
    exceptions: [],
};
// --- MCP: уровни/скоуп и эффективный набор (ADR-071 §2.2/§2.4) ---
export const MCP_SERVER_LEVELS = ['system', 'project', 'session'];
/**
 * ADR-071 §2.2: почему сервер отключён в эффективном наборе сессии:
 * - `system_disabled` — выключен системным мастер-флагом (`McpRegistryEntry.enabled === false`);
 * - `project_disabled` — не разрешён проектом (`project.enabled` — hard-gate);
 * - `session_optout` — явный opt-out в сессии (`mcpDisabledServers`);
 * - `not_default` — нет в снапшоте сессии (`mcpDefaultOn`) и не включён явно.
 *
 * Причина `config` отсутствует: `McpServerConfig.enabled` — только сид записи
 * реестра (`seedFromConfig`), в резолвинге/подключении не участвует.
 */
export const MCP_DISABLED_REASONS = [
    'system_disabled',
    'project_disabled',
    'session_optout',
    'not_default',
];
// --- ADR-105 (Ф3): запрет plaintext-секретов в заголовках MCP-коннектора ---
/**
 * Машинно-читаемый код ошибки записи реестра MCP с plaintext-секретом
 * в заголовках (ADR-105 §5, Ф3).
 *
 * HTTP: `400` с `error.code = "MCP_SECRET_HEADER_FORBIDDEN"`.
 */
export const MCP_SECRET_HEADER_ERROR_CODE = 'MCP_SECRET_HEADER_FORBIDDEN';
/**
 * Заголовки, значение которых — **секрет**. В реестре MCP (и в конфиге
 * коннектора) им не место: токен лежит в слое внешних доступов
 * (`ExternalCredential`, ADR-105), а коннектор ссылается на него по id
 * (`auth: { kind: 'credential', credentialId }`).
 *
 * Сравнение — регистронезависимое (HTTP-заголовки нечувствительны к регистру).
 */
export const FORBIDDEN_MCP_SECRET_HEADERS = [
    'authorization',
    'proxy-authorization',
    'x-api-key',
    'cookie',
];
/**
 * Найти запрещённые (секретные) заголовки в словаре `headers`.
 *
 * Возвращает **исходные имена** заголовков (как их записал пользователь) —
 * они уходят в сообщение об ошибке (значения — никогда).
 */
export function findForbiddenMcpSecretHeaders(headers) {
    if (!headers)
        return [];
    const forbidden = new Set(FORBIDDEN_MCP_SECRET_HEADERS);
    return Object.keys(headers).filter((name) => forbidden.has(name.trim().toLowerCase()));
}
/**
 * Текст ошибки «секретный заголовок в коннекторе» — **единый** для слоя
 * хранилища, HTTP-роутов (core/backend) и probe (ADR-105 §5, Ф3).
 *
 * Значений заголовков в тексте нет — только их имена.
 */
export function mcpSecretHeaderErrorMessage(forbidden) {
    return `Заголовки с секретами (${forbidden.join(', ')}) в MCP-коннекторе запрещены — используйте auth.kind='credential' (доступ к внешнему сервису)`;
}
